import { useState, useRef, useEffect } from 'react';
import { Users, X } from 'lucide-react';
import { usePresence } from './PresenceContext';
import { formatWhen } from './deviceLabel';

/**
 * "Who else is here" in the app header.
 *
 * RENDERS NOTHING WHEN NOBODY ELSE IS HERE. That is deliberate, and it is the
 * behaviour UnsentPayments beside it already has: a control reading "0 online" is
 * a permanent piece of furniture telling you nothing, whereas its absence says
 * the same thing and costs no space on a phone. It also means the header does not
 * grow a new widget for a dealership with one member of staff.
 *
 * Presence is open to every signed-in user, which is why this lives here rather
 * than in Settings — Settings > User Accounts sits behind users.manage, so a
 * salesperson could never reach it, and knowing a colleague is online is not an
 * administrative privilege.
 */
const PresenceIndicator = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { others, count } = usePresence();
  const dropdownRef = useRef(null);

  //Same click-outside shape as NotificationBell: mousedown rather than click, so
  //the panel closes on the press that starts elsewhere rather than waiting for
  //the release.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  //Closing the panel when the last colleague leaves, rather than leaving an empty
  //box on screen. Without this the panel outlives its own trigger.
  useEffect(() => { if (count === 0) setIsOpen(false); }, [count]);

  if (count === 0) return null;

  const initials = (u) =>
    `${(u.FirstName || '').charAt(0)}${(u.LastName || '').charAt(0)}`.toUpperCase() || '?';

  return (
    <div className="relative" ref={dropdownRef} id="presence-indicator">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 bg-white rounded-full hover:bg-gray-50 transition-all hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[color:var(--theme-ring)] shadow-sm"
        aria-label={count === 1 ? '1 colleague online' : `${count} colleagues online`}
        title={count === 1 ? '1 colleague online' : `${count} colleagues online`}
      >
        <Users className="w-5 h-5 text-gray-700" />
        <span
          className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-bold rounded-full border-2 border-gray-100 shadow-lg"
          style={{ background: 'var(--theme-success)', color: '#fff' }}
        >
          {count > 99 ? '99+' : count}
        </span>
      </button>

      {isOpen && (
        <div className="notif-panel absolute right-0 mt-3 w-72 max-w-[calc(100vw-16px)] bg-white rounded-xl shadow-2xl border border-gray-200 z-[100] max-h-[min(400px,70dvh)] flex flex-col animate-slideDown">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 rounded-t-xl">
            <h3 className="text-lg font-semibold text-gray-800">
              Here now
              <span className="ml-2 text-sm font-normal text-gray-500">({count})</span>
            </h3>
            <button
              onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
              className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="overflow-y-auto flex-1 custom-scrollbar divide-y divide-gray-100">
            {others.map((u) => (
              <div key={u.UserID} className="flex items-center gap-3 px-5 py-3">
                <span
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-semibold shrink-0"
                  style={{ background: 'var(--theme-accent-tint)', color: 'var(--theme-accent)' }}
                  aria-hidden="true"
                >
                  {initials(u)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-gray-800 truncate">
                    {`${u.FirstName || ''} ${u.LastName || ''}`.trim() || `User #${u.UserID}`}
                  </div>
                  {/* "Arrived", not "signed in": the server measures this from the
                      socket handshake, so it is when the app was opened, not when a
                      password was typed — which on a 30-day session can be weeks
                      apart. */}
                  <div className="text-xs text-gray-500">
                    {formatWhen(u.since) ? `Arrived ${formatWhen(u.since)}` : 'Here now'}
                  </div>
                </div>
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ background: 'var(--theme-success)' }}
                  aria-hidden="true"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PresenceIndicator;
